import { useTheme, Box, Fade, Stack } from "@mui/material";
import dynamic from "next/dynamic";

import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

import { useSanitizeHtmlString } from "@/common/helpers/html";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { getFormCategoryInfo } from "@/features/LiveFormEditor/common/formCategory.helper";
import { EmailContentPreview } from "@/features/LiveFormEditor/LivePreview/EmailPreview/EmailContentPreview/EmailContentPreview";

const TicketingThankYouPreview = dynamic(
  () => import("./TicketingThankYouPreview/TicketingThankYouPreview").then((m) => m.TicketingThankYouPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const AuctionWinnerPreview = dynamic(
  () => import("./AuctionEmailPreview/AuctionWinnerPreview").then((m) => m.AuctionWinnerPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const AuctionBidPlacedPreview = dynamic(
  () => import("./AuctionEmailPreview/AuctionBidPlacedPreview").then((m) => m.AuctionBidPlacedPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const AuctionNonWinnerPreview = dynamic(
  () => import("./AuctionEmailPreview/AuctionNonWinnerPreview").then((m) => m.AuctionNonWinnerPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const AuctionOutbidPreview = dynamic(
  () => import("./AuctionEmailPreview/AuctionOutbidPreview").then((m) => m.AuctionOutbidPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const ReadyForPickupPreview = dynamic(
  () => import("./FulfillmentEmailPreview/ReadyForPickupPreview").then((m) => m.ReadyForPickupPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const OrderShippedPreview = dynamic(
  () => import("./FulfillmentEmailPreview/OrderShippedPreview").then((m) => m.OrderShippedPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const ReminderPreview = dynamic(() => import("./ReminderPreview/ReminderPreview").then((m) => m.ReminderPreview), {
  ssr: false,
  loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" />,
});

const MembershipRenewalPreview = dynamic(
  () => import("./MembershipRenewalPreview/MembershipRenewalPreview").then((m) => m.MembershipRenewalPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

export const TicketingEmailLivePreview = () => {
  const { liveFormData } = useLiveFormEditorContext<"ticketing">();
  const emailInPreview = liveFormData?.automatedCommunicationsData.emailInPreview;

  return (
    <Fade key={emailInPreview ?? "none"} in appear timeout={250}>
      <Box sx={{ height: "100%", width: "100%" }}>
        <TicketingEmailLivePreviewContent />
      </Box>
    </Fade>
  );
};

const TicketingEmailLivePreviewContent = () => {
  const theme = useTheme();
  const { liveFormData, apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();

  const emailInPreview = liveFormData?.automatedCommunicationsData.emailInPreview;
  const isAIDefaultLoading = liveFormData?.automatedCommunicationsData.isAIDefaultLoading ?? false;

  const body: string = (() => {
    if (emailInPreview === "thank-you-bid-placed") {
      return liveFormData?.automatedCommunicationsData.thankYouBidPlaced?.body ?? "";
    }
    if (emailInPreview === "thank-you-non-winner") {
      return liveFormData?.automatedCommunicationsData.thankYouNonWinner?.body ?? "";
    }
    if (emailInPreview === "reminder-1") {
      return liveFormData?.automatedCommunicationsData.reminder1?.body ?? "";
    }
    if (emailInPreview === "reminder-2") {
      return liveFormData?.automatedCommunicationsData.reminder2?.body ?? "";
    }
    if (emailInPreview === "expiring-membership") {
      return liveFormData?.automatedCommunicationsData.expiringMembership?.body ?? "";
    }
    if (emailInPreview === "auto-renewal") {
      return liveFormData?.automatedCommunicationsData.autoRenewal?.body ?? "";
    }
    if (emailInPreview === "ready-for-pickup") {
      return liveFormData?.automatedCommunicationsData.readyForPickup?.body ?? "";
    }
    if (emailInPreview === "order-shipped") {
      return liveFormData?.automatedCommunicationsData.orderShipped?.body ?? "";
    }

    return liveFormData?.automatedCommunicationsData.thankYou?.body ?? "";
  })();
  const trimmedText = useSanitizeHtmlString(body, { preserveEmptyParagraphs: true }) ?? "";

  const { isAuction } = getFormCategoryInfo(apiTicketing.formCategory);

  if (!emailInPreview) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
        }}
        data-test="live-preview"
      />
    );
  }

  if (isAuction && emailInPreview === "outbid") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <AuctionOutbidPreview />
      </Box>
    );
  }

  if (emailInPreview === "reminder-1" || emailInPreview === "reminder-2") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <ReminderPreview reminderType={emailInPreview} />
      </Box>
    );
  }

  if (isAuction && emailInPreview === "thank-you-email") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <AuctionWinnerPreview />
      </Box>
    );
  }

  if (isAuction && emailInPreview === "thank-you-bid-placed") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <AuctionBidPlacedPreview />
      </Box>
    );
  }

  if (isAuction && emailInPreview === "thank-you-non-winner") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <AuctionNonWinnerPreview />
      </Box>
    );
  }

  if (emailInPreview === "expiring-membership" || emailInPreview === "auto-renewal") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <MembershipRenewalPreview kind={emailInPreview} />
      </Box>
    );
  }

  if (emailInPreview === "ready-for-pickup") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <ReadyForPickupPreview />
      </Box>
    );
  }

  if (emailInPreview === "order-shipped") {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <OrderShippedPreview />
      </Box>
    );
  }

  if (emailInPreview === "thank-you-email" && !isAuction) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        <TicketingThankYouPreview />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        height: "100%",
        backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
        overflowY: "auto",
      }}
      data-test="live-preview"
    >
      <Stack spacing={2} sx={{ marginY: { xs: 2, md: 10 }, marginX: { xs: 2, md: 15 } }}>
        <Stack
          spacing={2}
          id="live-preview-email"
          sx={{ backgroundColor: theme.palette.background.default, borderRadius: 1, padding: 3 }}
        >
          {isAIDefaultLoading ? (
            <Stack gap={1}>
              <DashboardSkeleton variant="rounded" width="100%" height={16} />
              <DashboardSkeleton variant="rounded" width="80%" height={16} />
              <DashboardSkeleton variant="rounded" width="60%" height={16} />
            </Stack>
          ) : (
            <EmailContentPreview html={trimmedText} data-test="email-live-preview-content" />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
